import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { PaymentMethod, type ShippingMethodEnum } from '../../shared/paymentTypes';
import { type ProductDetailContext } from '../types/types';

export enum LoadingPayPalEligibilityStatus {
    Pending = 'pending',
    NotStarted = 'notstarted',
    Done = 'done',
}

async function checkPayPalAvailability({
    checkPaymentMethodAvailability,
    setIsPayPalEligible,
    setLoading,
    setWasEverEligible,
    shippingMethod,
    total,
}: {
    checkPaymentMethodAvailability: ProductDetailContext['checkPaymentMethodAvailability'];
    setIsPayPalEligible: Dispatch<SetStateAction<boolean>>;
    setLoading: Dispatch<SetStateAction<LoadingPayPalEligibilityStatus>>;
    setWasEverEligible: Dispatch<SetStateAction<boolean>>;
    shippingMethod: ShippingMethodEnum;
    total: number;
}): Promise<void> {
    try {
        setLoading(LoadingPayPalEligibilityStatus.Pending);
        const { available } = await checkPaymentMethodAvailability(
            PaymentMethod.PayPal,
            total,
            shippingMethod,
        );

        setIsPayPalEligible(available);
        if (available) {
            setWasEverEligible(true);
        }

        setLoading(LoadingPayPalEligibilityStatus.Done);
    } catch {
        setIsPayPalEligible(false);
        setLoading(LoadingPayPalEligibilityStatus.Done);
    }
}

function usePayPalExpressEligibility(totalAmount: number, shippingMethod: ShippingMethodEnum) {
    const { checkPaymentMethodAvailability } = useFragmentContext<ProductDetailContext>();
    const [isPayPalEligible, setIsPayPalEligible] = useState(false);
    const [loading, setLoading] = useState(LoadingPayPalEligibilityStatus.NotStarted);
    const [wasEverEligible, setWasEverEligible] = useState(false);

    useEffect(() => {
        if (!totalAmount) {
            setLoading(LoadingPayPalEligibilityStatus.Done);
            setIsPayPalEligible(false);
            return;
        }

        void checkPayPalAvailability({
            checkPaymentMethodAvailability,
            setIsPayPalEligible,
            setLoading,
            setWasEverEligible,
            shippingMethod,
            total: totalAmount,
        });
    }, [checkPaymentMethodAvailability, shippingMethod, totalAmount]);

    return { isPayPalEligible, loading, wasEverEligible };
}

export { usePayPalExpressEligibility };
